import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { deleteLitter, type Litter } from '@/api/entities/litters';
import { ExtractFnReturnType } from '@/lib/react-query';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { GraphQLRequestError } from '@/api/graphql';

type QueryFnType = typeof deleteLitter;

type UseDeleteLitterOptions = {
  config?: Omit<
    UseMutationOptions<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, Litter>,
    'mutationFn'
  >;
};

export const useDeleteLitter = ({ config }: UseDeleteLitterOptions = {}) => {
  const queryClient = useQueryClient();
  const { getAccessToken } = useCognitoAuth();

  return useMutation<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, Litter>({
    ...config,
    mutationFn: async (litter: Litter) => {
      return deleteLitter(litter.id, getAccessToken);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['litters'] });
      queryClient.invalidateQueries({ queryKey: ['litters', variables.id] });
      config?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
